import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lightbulb, CheckCircle, Circle, ArrowRight, Sparkles, Loader2, Send } from 'lucide-react';
import { CJMSClient } from '../../api/cjmsApi';
import { feedbackBus } from '../../services/feedbackBus';

interface CoCreationWizardProps {
    client: CJMSClient;
}

const CO_CREATION_AREAS = [
    { id: 'feature', label: 'Feature Request', desc: 'What would make the platform better?', icon: Lightbulb },
    { id: 'content', label: 'Co-Create Content', desc: 'Case study, testimonial, or guest post', icon: Sparkles },
    { id: 'process', label: 'Process Improvement', desc: 'How can we improve our service?', icon: Lightbulb },
];

export const CoCreationWizard: React.FC<CoCreationWizardProps> = ({ client }) => {
    const isEligible = client.currentStage === 'Advocacy';
    const [step, setStep] = useState(0);
    const [selectedArea, setSelectedArea] = useState<string | null>(null);
    const [idea, setIdea] = useState('');
    const [commitment, setCommitment] = useState('');
    const [status, setStatus] = useState<'idle' | 'submitting' | 'submitted'>('idle');

    if (!isEligible) return null;

    const handleNext = () => {
        if (step < 3) setStep(step + 1);
    };

    const handleBack = () => {
        if (step > 0) setStep(step - 1);
    };

    const handleSubmit = async () => {
        setStatus('submitting');
        await new Promise(r => setTimeout(r, 800));
        feedbackBus.emit('activity_completed', {
            activityLabel: `Co-creation submitted: ${selectedArea}`,
            clientEmail: client.email,
        });
        setStatus('submitted');
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        >
            <div className="flex items-center gap-2 p-4 border-b border-slate-100">
                <Sparkles className="w-4 h-4 text-purple-600" />
                <h3 className="text-sm font-bold text-slate-800">Co-Creation Wizard</h3>
                <div className="flex gap-1 ml-auto">
                    {[0, 1, 2, 3].map(i => (
                        <div key={i} className={`w-2 h-2 rounded-full ${i <= step ? 'bg-purple-500' : 'bg-slate-200'}`} />
                    ))}
                </div>
            </div>
            <div className="p-4 space-y-4">
                {status === 'submitted' ? (
                    <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-100">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-xs text-green-700 font-medium">Co-creation idea submitted! We will follow up.</span>
                    </div>
                ) : (
                    <>
                        {step === 0 && (
                            <div className="space-y-3">
                                <p className="text-xs text-slate-600">What type of co-creation opportunity would you like to explore?</p>
                                {CO_CREATION_AREAS.map(area => (
                                    <button
                                        key={area.id}
                                        onClick={() => { setSelectedArea(area.id); }}
                                        className={`w-full p-3 rounded-lg border text-left transition-all ${
                                            selectedArea === area.id
                                                ? 'border-purple-400 bg-purple-50'
                                                : 'border-slate-100 bg-white hover:border-purple-200'
                                        }`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <area.icon className="w-4 h-4 text-purple-600" />
                                            <span className="text-xs font-semibold text-slate-700">{area.label}</span>
                                            {selectedArea === area.id && <CheckCircle className="w-3 h-3 text-purple-500 ml-auto" />}
                                        </div>
                                        <p className="text-[10px] text-slate-500 mt-1 ml-6">{area.desc}</p>
                                    </button>
                                ))}
                            </div>
                        )}
                        {step === 1 && (
                            <div className="space-y-3">
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Describe Your Idea</label>
                                <textarea
                                    value={idea}
                                    onChange={e => setIdea(e.target.value)}
                                    placeholder={`Tell us what you have in mind for ${selectedArea}...`}
                                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg resize-none h-28 focus:ring-1 focus:ring-purple-400 outline-none"
                                />
                            </div>
                        )}
                        {step === 2 && (
                            <div className="space-y-3">
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Your Commitment</label>
                                <textarea
                                    value={commitment}
                                    onChange={e => setCommitment(e.target.value)}
                                    placeholder="How would you like to contribute? (time, resources, expertise...)"
                                    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg resize-none h-20 focus:ring-1 focus:ring-purple-400 outline-none"
                                />
                            </div>
                        )}
                        {step === 3 && (
                            <div className="space-y-4">
                                <div className="p-3 bg-purple-50 rounded-lg border border-purple-100 space-y-2">
                                    <div className="flex items-center gap-2">
                                        <Lightbulb className="w-4 h-4 text-purple-600" />
                                        <span className="text-xs font-semibold text-purple-700">Review & Submit</span>
                                    </div>
                                    <div className="text-[10px] text-slate-600 space-y-1">
                                        <p><span className="font-semibold">Area:</span> {CO_CREATION_AREAS.find(a => a.id === selectedArea)?.label}</p>
                                        <p><span className="font-semibold">Idea:</span> {idea}</p>
                                        <p><span className="font-semibold">Commitment:</span> {commitment}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={handleSubmit}
                                    disabled={status === 'submitting'}
                                    className="w-full px-4 py-2 text-xs font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
                                >
                                    {status === 'submitting' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                    Submit Co-Creation Idea
                                </button>
                            </div>
                        )}
                        <div className="flex justify-between pt-2">
                            <button
                                onClick={handleBack}
                                disabled={step === 0}
                                className="text-[11px] text-slate-500 hover:text-slate-700 disabled:opacity-30"
                            >
                                Back
                            </button>
                            {step < 3 && (
                                <button
                                    onClick={handleNext}
                                    disabled={(step === 0 && !selectedArea) || (step === 1 && !idea.trim()) || (step === 2 && !commitment.trim())}
                                    className="px-3 py-1.5 text-[11px] font-medium bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                                >
                                    Next <ArrowRight className="w-3 h-3" />
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </motion.div>
    );
};
